// Shared building blocks for the "Ghidha Immersive" cockpit aesthetic,
// reused across the Hayah landscape views (Ghidha, Fasting, Muraqabah,
// Exercise). Provides palette, font stacks, ambient backdrop layers,
// drifting embers, glass-card corner brackets, and a micro-heading line.

import { useEffect, useRef, useState } from "react";

// Design palette
export const IMM = {
  amber:    "#E0A030",
  amberSoft:"#F0C26B",
  amberDeep:"#A87420",
  cream:    "#E8E2D2",
  ink1:     "#F4EFE0",
  ink2:     "#C9C2B0",
  ink3:     "#827E72",
  ink4:     "#4A483F",
  line:     "#22222C",
  lineSoft: "#1A1A22",
  coralSoft:"#D97A6A",
  coral:    "#C24A3A",
  sage:     "#7CC4A0",
  teal:     "#4FD9C2",
  bg0:      "#050507",
  bg1:      "#0A0A0E",
};
export const IMM_MONO  = '"JetBrains Mono",ui-monospace,SFMono-Regular,Menlo,monospace';
export const IMM_SERIF = '"Fraunces","Instrument Serif",Georgia,serif';
export const IMM_ITALIC = '"Instrument Serif",Georgia,serif';
export const IMM_ARABIC = "'Scheherazade New', serif";

// Drift/blink keyframes — injected once per immersive wrapper
export const IMM_KEYFRAMES = `
@keyframes immBloom {
  0% { transform: translate(0, 0) scale(1); }
  100% { transform: translate(20px, -14px) scale(1.08); }
}
@keyframes immBreath {
  0%, 100% { filter: drop-shadow(0 0 14px rgba(224,160,48,0.4)); }
  50%      { filter: drop-shadow(0 0 26px rgba(224,160,48,0.7)); }
}
@keyframes immLiveBlink {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.45; }
}
@keyframes immFloatUp {
  0%   { transform: translateY(0) translateX(0); opacity: 0; }
  10%  { opacity: 0.55; }
  90%  { opacity: 0.35; }
  100% { transform: translateY(-130%) translateX(30px); opacity: 0; }
}
`;

// Layered ambient backdrop (bloom + grid + vignette).
export function Ambient() {
  return (
    <>
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0,
        background:
          "radial-gradient(circle at 28% 38%, rgba(224,160,48,0.20) 0%, transparent 38%)," +
          "radial-gradient(circle at 78% 70%, rgba(124,196,160,0.10) 0%, transparent 42%)," +
          "radial-gradient(circle at 50% 100%, rgba(224,160,48,0.10) 0%, transparent 50%)",
        animation: "immBloom 22s ease-in-out infinite alternate",
      }}/>
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0,
        backgroundImage:
          "linear-gradient(rgba(224,160,48,0.025) 1px, transparent 1px)," +
          "linear-gradient(90deg, rgba(224,160,48,0.025) 1px, transparent 1px)",
        backgroundSize: "48px 48px",
        WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 50%, #000 0%, transparent 80%)",
        maskImage: "radial-gradient(ellipse 80% 60% at 50% 50%, #000 0%, transparent 80%)",
      }}/>
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0,
        background: "radial-gradient(ellipse 90% 70% at 50% 50%, transparent 40%, rgba(0,0,0,0.55) 100%)",
      }}/>
    </>
  );
}

// Drifting embers — random amber particles rising slowly.
export function Embers({ count = 12 }) {
  const seeds = useRef(null);
  if (!seeds.current) {
    seeds.current = Array.from({ length: count }, () => ({
      size: 1.5 + Math.random() * 2.5,
      left: Math.random() * 100,
      dur: 14 + Math.random() * 18,
      delay: -Math.random() * 25,
      opacity: 0.35 + Math.random() * 0.4,
    }));
  }
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0, overflow: "hidden" }}>
      {seeds.current.map((s, i) => (
        <div key={i} style={{
          position: "absolute",
          width: s.size, height: s.size,
          left: `${s.left}%`, bottom: "-8%",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(224,160,48,0.7), transparent 70%)",
          opacity: s.opacity,
          animation: `immFloatUp ${s.dur}s linear ${s.delay}s infinite`,
        }}/>
      ))}
    </div>
  );
}

// Cockpit shell — full-bleed dark wrapper with ambient backdrop. Children
// render in a positioned content layer above. Gives every immersive screen
// the same atmospheric feel.
export function ImmersiveShell({ children, padding = "10px 16px", style = {} }) {
  return (
    <div style={{
      position: "relative",
      width: "100%", height: "100%",
      flex: 1, minHeight: 0,
      borderRadius: 20,
      overflow: "hidden",
      background: `linear-gradient(180deg, ${IMM.bg0} 0%, ${IMM.bg1} 100%)`,
      border: "0.5px solid rgba(255,255,255,0.05)",
      boxShadow: "0 10px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)",
      color: IMM.ink1,
      display: "flex", flexDirection: "column",
      ...style,
    }}>
      <style>{IMM_KEYFRAMES}</style>
      <Ambient/>
      <Embers/>
      <div style={{ position: "relative", zIndex: 2, flex: 1, minHeight: 0, display: "flex", flexDirection: "column", padding }}>
        {children}
      </div>
    </div>
  );
}

// Glass-card corner brackets (amber).
export function CardCorners() {
  const base = {
    position: "absolute", width: 10, height: 10,
    borderColor: "rgba(224,160,48,0.5)", borderStyle: "solid", borderWidth: 0,
    pointerEvents: "none", zIndex: 0,
  };
  return (
    <>
      <div style={{ ...base, top: 8, left: 8, borderTopWidth: 1, borderLeftWidth: 1 }}/>
      <div style={{ ...base, top: 8, right: 8, borderTopWidth: 1, borderRightWidth: 1 }}/>
      <div style={{ ...base, bottom: 8, left: 8, borderBottomWidth: 1, borderLeftWidth: 1 }}/>
      <div style={{ ...base, bottom: 8, right: 8, borderBottomWidth: 1, borderRightWidth: 1 }}/>
    </>
  );
}

// Numbered eyebrow rule heading.
export function MicroHd({ num, label, right }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      fontFamily: IMM_MONO, fontSize: 9, letterSpacing: "0.18em",
      textTransform: "uppercase", color: IMM.ink3,
    }}>
      <span style={{ color: IMM.amber, fontWeight: 600 }}>{num}</span>
      <span style={{ flex: 1, height: 0.5, background: `linear-gradient(90deg, ${IMM.line}, transparent)` }}/>
      <span style={{ color: IMM.ink2 }}>{label}</span>
      {right && <span style={{ color: IMM.ink4 }}>· {right}</span>}
    </div>
  );
}

// Standard glass card style — used as a CSS object spread.
export const immCard = {
  position: "relative",
  background: "linear-gradient(180deg, rgba(20,20,26,0.55), rgba(10,10,14,0.35))",
  border: "0.5px solid rgba(255,255,255,0.06)",
  borderRadius: 16,
  backdropFilter: "blur(12px) saturate(140%)",
  WebkitBackdropFilter: "blur(12px) saturate(140%)",
  overflow: "hidden",
};

// Useful timing hook for animated values (eased).
export function useImmAnim(target, dur = 1200, delay = 0) {
  const [v, setV] = useState(0);
  useEffect(() => {
    setV(0);
    let raf, start;
    const tid = setTimeout(() => {
      const step = (ts) => {
        if (start === undefined) start = ts;
        const t = Math.min(1, (ts - start) / dur);
        const eased = 1 - Math.pow(1 - t, 4);
        setV(target * eased);
        if (t < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => { clearTimeout(tid); if (raf) cancelAnimationFrame(raf); };
  }, [target, dur, delay]);
  return v;
}
